import React, { useState, useEffect } from 'react';
import './UserManagement.css';
import NavQueue from './NavQueue';
import ReleaseNotes from './ReleaseNotes';
import { currentDeepLink, writeDeepLink } from './deepLink';
import EmployeePicker, { buildEmployeeOptions } from './EmployeePicker';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';
const API_KEY = process.env.REACT_APP_API_KEY;

const ACCESS_LEVEL_READ = 'Read';
const ACCESS_LEVEL_READ_WRITE = 'Read/Write';

// Mirrors the picklist in reminderDays.js on the server, which is the authority
// on what CRM will accept — this copy only fixes display order and abbreviations.
const REMINDER_DAYS = [
  { value: 'Mandag', short: 'Man' },
  { value: 'Tirsdag', short: 'Tir' },
  { value: 'Onsdag', short: 'Ons' },
  { value: 'Torsdag', short: 'Tor' },
  { value: 'Fredag', short: 'Fre' }
];
const ALL_REMINDER_DAYS = REMINDER_DAYS.map(d => d.value);

function employeeReminderDays(emp) {
  return Array.isArray(emp?.Reminder_dage) ? emp.Reminder_dage : [];
}

// Keeps a day set in weekday order no matter what order it was clicked in, so
// the pills and the value written to CRM always read Monday-first.
function sortReminderDays(days) {
  return ALL_REMINDER_DAYS.filter(day => days.includes(day));
}

const USER_GROUPS = {
  activated: { label: 'Gets Checked', emptyLabel: 'No activated users', tileClass: '' },
  massDeactivated: { label: 'Mass Unlocked', emptyLabel: 'No mass deactivated users', tileClass: 'stat-tile--warning' },
  manuallyDeactivated: { label: 'Manually Unlocked', emptyLabel: 'No manually deactivated users', tileClass: 'stat-tile--danger' }
};

function emailInitials(email) {
  const local = (email || '').split('@')[0] || '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase() || '?';
}

function delegateGroupKey(delegate) {
  return delegate.id || delegate.email || delegate.name;
}

const IconUsers = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-2.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4" />
  </svg>
);
const IconClock = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconKey = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 11-12 0 6 6 0 0112 0zM3 21v-1a4 4 0 014-4h.5" />
  </svg>
);
const IconBell = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.158c0 .538-.214 1.055-.595 1.437L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);
const IconSend = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);
const IconNotes = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);
const IconSun = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.36 6.36l-.7-.7M6.34 6.34l-.7-.7m12.02 0l-.7.7M6.34 17.66l-.7.7M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);
const IconMoon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);
const IconSignOut = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

// Searchable, type-to-filter picker for choosing an employee (or, with
// `multi`, any number of them). Options are filtered client-side against
// name/email. Closing on outside-click (rather than input blur) avoids the
// blur-before-click race that would otherwise swallow the click.
// Custom confirm dialog used in place of window.confirm so destructive
// actions match the app's own styling instead of the browser chrome.
function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel }) {
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-modal-title" onMouseDown={e => e.stopPropagation()}>
        <h3 id="confirm-modal-title" className="modal-title">{title}</h3>
        <p className="modal-message">{message}</p>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const [theme, setTheme] = useState(() => {
    const saved = window.localStorage.getItem('mw-theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    window.localStorage.setItem('mw-theme', theme);
  }, [theme]);

  const [config, setConfig] = useState({
    activated: [],
    massDeactivated: [],
    manuallyDeactivated: []
  });
  // The Nav failure mail links straight to a rejected timelog, so ?tab= decides
  // where the app opens. Read once at mount: after that the sidebar owns it.
  const [activeTab, setActiveTab] = useState(() => currentDeepLink().tab || 'users');

  // Every sidebar click goes through here so the URL always mirrors the screen,
  // which is what makes a tab shareable and survives a refresh. NavQueue writes
  // its own view on top of this when it is the active tab.
  const selectTab = (tab) => {
    setActiveTab(tab);
    writeDeepLink({ tab });
  };
  const [userFilter, setUserFilter] = useState('activated');
  const [employees, setEmployees] = useState({}); // email -> { id, name, Non_billable_hours_yearly_cap }
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [editingCap, setEditingCap] = useState({});
  const [savingCap, setSavingCap] = useState(new Set());
  const [searchCaps, setSearchCaps] = useState('');
  const [selectedActivated, setSelectedActivated] = useState(new Set());
  const [selectedMassDeactivated, setSelectedMassDeactivated] = useState(new Set());
  const [selectedManuallyDeactivated, setSelectedManuallyDeactivated] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [searchActivated, setSearchActivated] = useState('');
  const [searchMassDeactivated, setSearchMassDeactivated] = useState('');
  const [searchManuallyDeactivated, setSearchManuallyDeactivated] = useState('');

  // Reminders tab
  const [searchReminders, setSearchReminders] = useState('');
  const [selectedReminders, setSelectedReminders] = useState(new Set());
  const [savingReminders, setSavingReminders] = useState(new Set());
  const [draftReminderDays, setDraftReminderDays] = useState([]);

  const [authenticated, setAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [user, setUser] = useState(null);

  // Timelog Access tab
  const [accessGrants, setAccessGrants] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [searchAccess, setSearchAccess] = useState('');
  const [grantEmployeeEmails, setGrantEmployeeEmails] = useState([]);
  const [grantDelegateEmail, setGrantDelegateEmail] = useState('');
  const [grantAccessLevel, setGrantAccessLevel] = useState(ACCESS_LEVEL_READ);
  const [grantSubmitting, setGrantSubmitting] = useState(false);
  const [grantError, setGrantError] = useState(null);
  const [grantSuccess, setGrantSuccess] = useState(null);
  const [deletingGrantId, setDeletingGrantId] = useState(null);
  const [deletingGroupKey, setDeletingGroupKey] = useState(null);
  const [expandedDelegates, setExpandedDelegates] = useState(new Set());
  const [groupPendingRevoke, setGroupPendingRevoke] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if ((activeTab === 'caps' || activeTab === 'access' || activeTab === 'reminders' || activeTab === 'nav-queue') && authenticated) {
      fetchEmployees();
    }
  }, [activeTab, authenticated]);

  useEffect(() => {
    if (activeTab === 'access' && authenticated) {
      fetchAccessGrants();
    }
  }, [activeTab, authenticated]);

  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/mw/check-auth`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.authenticated && data.authorized) {
        setAuthenticated(true);
        setAuthError(null);
        setUser(data.user || null);
        fetchConfig();
      } else if (data.error) {
        setAuthenticated(false);
        setAuthError(data.error);
      } else {
        setAuthenticated(false);
        setAuthError(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setAuthenticated(false);
      setAuthError('Failed to check authentication');
    } finally {
      setAuthChecking(false);
    }
  };

  const handleLogin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/mw/login`, {
        credentials: 'include'
      });
      const data = await response.json();

      const popup = window.open(data.url, 'msAuth', 'width=600,height=700');

      window.addEventListener('message', function handleOAuthMsg(event) {
        if (event.data && event.data.type === 'oauth-success') {
          popup.close();
          // Recheck auth after successful OAuth
          checkAuth();
          window.removeEventListener('message', handleOAuthMsg);
        } else if (event.data && event.data.type === 'oauth-error') {
          popup.close();
          setAuthError(event.data.message || 'Authorization failed');
          window.removeEventListener('message', handleOAuthMsg);
        }
      });
    } catch (err) {
      setError('Failed to initiate login');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/mw/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) {
      // ignore — falling through to a full reload resets client state regardless
    }
    window.location.reload();
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/mw/users-config`, {
        headers: {
          'X-API-Key': API_KEY
        },
        credentials: 'include' // Important for session cookies
      });

      if (response.status === 401) {
        setAuthenticated(false);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch users configuration: ${response.status}`);
      }

      const data = await response.json();
      setConfig(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle selection for activated users
  const toggleActivated = (email) => {
    setSelectedActivated(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  // Toggle selection for mass deactivated users
  const toggleMassDeactivated = (email) => {
    setSelectedMassDeactivated(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  // Toggle selection for manually deactivated users
  const toggleManuallyDeactivated = (email) => {
    setSelectedManuallyDeactivated(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  // Mass deactivate all activated users
  const handleMassDeactivate = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`${API_BASE_URL}/mw/mass-deactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        credentials: 'include',
        body: JSON.stringify({ emails: config.activated })
      });

      if (!response.ok) throw new Error('Failed to mass deactivate users');

      const data = await response.json();
      setConfig(data.config);
      setSelectedActivated(new Set());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Manually deactivate selected users
  const handleManualDeactivate = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`${API_BASE_URL}/mw/manual-deactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        credentials: 'include',
        body: JSON.stringify({ emails: Array.from(selectedActivated) })
      });

      if (!response.ok) throw new Error('Failed to manually deactivate users');

      const data = await response.json();
      setConfig(data.config);
      setSelectedActivated(new Set());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Mass activate from mass deactivated
  const handleMassActivate = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`${API_BASE_URL}/mw/mass-activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        credentials: 'include',
        body: JSON.stringify({ emails: config.massDeactivated })
      });

      if (!response.ok) throw new Error('Failed to mass activate users');

      const data = await response.json();
      setConfig(data.config);
      setSelectedMassDeactivated(new Set());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Activate selected mass deactivated users
  const handleActivateFromMassDeactivated = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`${API_BASE_URL}/mw/activate-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        credentials: 'include',
        body: JSON.stringify({ emails: Array.from(selectedMassDeactivated) })
      });

      if (!response.ok) throw new Error('Failed to activate users');

      const data = await response.json();
      setConfig(data.config);
      setSelectedMassDeactivated(new Set());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Mass activate from manually deactivated
  const handleMassActivateManual = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`${API_BASE_URL}/mw/activate-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        credentials: 'include',
        body: JSON.stringify({ emails: config.manuallyDeactivated })
      });

      if (!response.ok) throw new Error('Failed to mass activate users');

      const data = await response.json();
      setConfig(data.config);
      setSelectedManuallyDeactivated(new Set());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Activate selected manually deactivated users
  const handleActivateFromManual = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`${API_BASE_URL}/mw/activate-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        credentials: 'include',
        body: JSON.stringify({ emails: Array.from(selectedManuallyDeactivated) })
      });

      if (!response.ok) throw new Error('Failed to activate users');

      const data = await response.json();
      setConfig(data.config);
      setSelectedManuallyDeactivated(new Set());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await fetch(`${API_BASE_URL}/mw/employees`, {
        headers: { 'X-API-Key': API_KEY },
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      setEmployees(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleCapChange = (email, value) => {
    setEditingCap(prev => ({ ...prev, [email]: value }));
  };

  const handleCapSave = async (email) => {
    const rawValue = editingCap[email];
    if (rawValue === undefined) return;
    const cap = rawValue === '' ? null : Number(rawValue);
    if (rawValue !== '' && (isNaN(cap) || cap < 0)) return;

    try {
      setSavingCap(prev => new Set(prev).add(email));
      const response = await fetch(`${API_BASE_URL}/mw/update-employee-cap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        credentials: 'include',
        body: JSON.stringify({ email, cap })
      });
      if (!response.ok) throw new Error('Failed to update cap');
      const data = await response.json();
      setEmployees(prev => ({
        ...prev,
        [email]: { ...(prev[email] || {}), Non_billable_hours_yearly_cap: data.Non_billable_hours_yearly_cap }
      }));
      setEditingCap(prev => { const n = { ...prev }; delete n[email]; return n; });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingCap(prev => { const n = new Set(prev); n.delete(email); return n; });
    }
  };

  // Writes the same day set to every listed employee. A round trip to CRM takes
  // long enough that waiting on it would make each pill feel broken, so the
  // pills flip straight away and roll back to the snapshot if the write fails.
  const applyReminderDays = async (emails, days) => {
    if (emails.length === 0) return;
    const snapshot = {};
    emails.forEach(email => { snapshot[email] = employeeReminderDays(employees[email]); });

    const writeDays = (byEmail) => setEmployees(prev => {
      const next = { ...prev };
      emails.forEach(email => {
        next[email] = { ...(next[email] || {}), Reminder_dage: byEmail(email) };
      });
      return next;
    });

    writeDays(() => days);
    setSavingReminders(prev => { const n = new Set(prev); emails.forEach(e => n.add(e)); return n; });

    try {
      const response = await fetch(`${API_BASE_URL}/mw/reminder-days`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        credentials: 'include',
        body: JSON.stringify({ emails, days })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to update reminder days');
      setError(null);
    } catch (err) {
      writeDays(email => snapshot[email]);
      setError(err.message);
    } finally {
      setSavingReminders(prev => { const n = new Set(prev); emails.forEach(e => n.delete(e)); return n; });
    }
  };

  const toggleReminderDay = (email, day) => {
    const current = employeeReminderDays(employees[email]);
    const next = current.includes(day)
      ? current.filter(d => d !== day)
      : sortReminderDays([...current, day]);
    applyReminderDays([email], next);
  };

  // One button per row rather than separate All and None buttons: whichever
  // action is available is always the one shown.
  const toggleReminderRowAll = (email) => {
    const current = employeeReminderDays(employees[email]);
    applyReminderDays([email], current.length === ALL_REMINDER_DAYS.length ? [] : ALL_REMINDER_DAYS);
  };

  const toggleDraftReminderDay = (day) => {
    setDraftReminderDays(prev => (
      prev.includes(day) ? prev.filter(d => d !== day) : sortReminderDays([...prev, day])
    ));
  };

  const toggleReminderSelected = (email) => {
    setSelectedReminders(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const fetchAccessGrants = async () => {
    try {
      setLoadingAccess(true);
      const response = await fetch(`${API_BASE_URL}/mw/timelog-access`, {
        headers: { 'X-API-Key': API_KEY },
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch timelog access grants');
      const data = await response.json();
      setAccessGrants(data.grants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAccess(false);
    }
  };

  // Picking a delegate who's already checked in the employee multi-select
  // would otherwise create a self-grant; drop them from that list instead.
  const handleDelegateChange = (email) => {
    setGrantDelegateEmail(email);
    setGrantEmployeeEmails(prev => prev.filter(e => e !== email));
  };

  // Always goes through the bulk-create endpoint — even a single employee is
  // just a one-item array — so there's one code path instead of a separate
  // single-grant API.
  const handleCreateGrant = async (e) => {
    e.preventDefault();
    setGrantError(null);
    setGrantSuccess(null);

    const delegate = employees[grantDelegateEmail];
    if (!delegate) {
      setGrantError('Choose a delegate.');
      return;
    }
    if (grantEmployeeEmails.length === 0) {
      setGrantError('Choose at least one employee.');
      return;
    }

    const employeeIds = grantEmployeeEmails
      .map(email => employees[email]?.id)
      .filter(Boolean);

    try {
      setGrantSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/mw/timelog-access/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        credentials: 'include',
        body: JSON.stringify({
          employeeIds,
          delegateId: delegate.id,
          accessLevel: grantAccessLevel
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create access grant(s)');

      const verb = grantAccessLevel === ACCESS_LEVEL_READ_WRITE ? 'view and edit' : 'view';
      const skippedNote = data.skipped ? ` (${data.skipped} already had access)` : '';
      setGrantSuccess(
        `${delegate.name || grantDelegateEmail} can now ${verb} ${data.created} ${data.created === 1 ? 'employee' : 'employees'}' timelogs${skippedNote}.`
      );
      setGrantEmployeeEmails([]);
      setGrantDelegateEmail('');
      setGrantAccessLevel(ACCESS_LEVEL_READ);
      fetchAccessGrants();
    } catch (err) {
      setGrantError(err.message);
    } finally {
      setGrantSubmitting(false);
    }
  };

  const handleDeleteGrant = async (grant) => {
    if (!window.confirm(`Revoke ${grant.delegate.name}'s access to ${grant.employee.name}'s timelogs?`)) return;
    try {
      setDeletingGrantId(grant.id);
      const response = await fetch(`${API_BASE_URL}/mw/timelog-access/${grant.id}`, {
        method: 'DELETE',
        headers: { 'X-API-Key': API_KEY },
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to revoke access grant');
      setAccessGrants(prev => prev.filter(g => g.id !== grant.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingGrantId(null);
    }
  };

  const toggleGroupExpanded = (key) => {
    setExpandedDelegates(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDeleteGrantGroup = (group) => {
    setGroupPendingRevoke(group);
  };

  const confirmDeleteGrantGroup = async () => {
    const group = groupPendingRevoke;
    if (!group) return;
    setGroupPendingRevoke(null);
    const groupKey = delegateGroupKey(group.delegate);
    try {
      setDeletingGroupKey(groupKey);
      const ids = group.items.map(g => g.id);
      const response = await fetch(`${API_BASE_URL}/mw/timelog-access/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        credentials: 'include',
        body: JSON.stringify({ ids })
      });
      if (!response.ok) throw new Error('Failed to revoke access grants');
      const idSet = new Set(ids);
      setAccessGrants(prev => prev.filter(g => !idSet.has(g.id)));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingGroupKey(null);
    }
  };

  const filterUsers = (users, searchTerm) => {
    if (!searchTerm) return users;
    return users.filter(email =>
      email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredActivated = filterUsers(config.activated, searchActivated);
  const filteredMassDeactivated = filterUsers(config.massDeactivated, searchMassDeactivated);
  const filteredManuallyDeactivated = filterUsers(config.manuallyDeactivated, searchManuallyDeactivated);

  const themeToggleButton = (
    <button className="theme-toggle" onClick={toggleTheme}>
      {theme === 'dark' ? <IconSun /> : <IconMoon />}
      <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
    </button>
  );

  // Show login screen if not authenticated
  if (authChecking) {
    return (
      <div className="auth-page" data-theme={theme}>
        <div className="loading">
          <div className="loading-spinner" />
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="auth-page" data-theme={theme}>
        <div className="login-container">
          <div className="login-mark"><img src="/mw-logo.png" alt="MW" /></div>
          <h1>Portal Admin</h1>
          {authError ? (
            <>
              <div className="error-banner" style={{ marginBottom: '4px' }}>
                <div className="error-content">
                  <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <strong>Access Denied:</strong> {authError}
                </div>
              </div>
              <p>Please contact an administrator if you believe this is an error.</p>
            </>
          ) : (
            <>
              <p>Please log in with your Microsoft account to continue</p>
              <button onClick={handleLogin} className="btn btn-primary">
                Login with Microsoft
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="auth-page" data-theme={theme}>
        <div className="loading">
          <div className="loading-spinner" />
          <p>Loading users configuration...</p>
        </div>
      </div>
    );
  }

  const allEmployeeEmails = Array.from(new Set([
    ...config.activated,
    ...config.massDeactivated,
    ...config.manuallyDeactivated
  ])).sort();

  const filteredCapEmails = searchCaps
    ? allEmployeeEmails.filter(e => e.toLowerCase().includes(searchCaps.toLowerCase()))
    : allEmployeeEmails;

  const employeeOptions = buildEmployeeOptions(employees);

  const reminderRows = searchReminders
    ? employeeOptions.filter(o => {
        const q = searchReminders.toLowerCase();
        return o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q);
      })
    : employeeOptions;

  const allRemindersSelected = reminderRows.length > 0 && reminderRows.every(o => selectedReminders.has(o.email));
  const someRemindersSelected = reminderRows.some(o => selectedReminders.has(o.email));
  const selectedReminderEmails = Array.from(selectedReminders);

  const handleReminderSelectAllToggle = () => {
    setSelectedReminders(prev => {
      const next = new Set(prev);
      if (allRemindersSelected) reminderRows.forEach(o => next.delete(o.email));
      else reminderRows.forEach(o => next.add(o.email));
      return next;
    });
  };

  const filteredAccessGrants = searchAccess
    ? accessGrants.filter(g => {
        const q = searchAccess.toLowerCase();
        return (
          g.employee.name.toLowerCase().includes(q) ||
          (g.employee.email || '').toLowerCase().includes(q) ||
          g.delegate.name.toLowerCase().includes(q) ||
          (g.delegate.email || '').toLowerCase().includes(q)
        );
      })
    : accessGrants;

  // One record per delegate, listing every employee they've been granted
  // access to — rather than a flat list with one row per employee/delegate pair.
  const grantsByDelegate = (() => {
    const groups = new Map();
    for (const grant of filteredAccessGrants) {
      const key = grant.delegate.id || grant.delegate.email || grant.delegate.name;
      if (!groups.has(key)) groups.set(key, { delegate: grant.delegate, items: [] });
      groups.get(key).items.push(grant);
    }
    return [...groups.values()].sort((a, b) => a.delegate.name.localeCompare(b.delegate.name, 'da', { sensitivity: 'base' }));
  })();

  const userInitial = (user?.Display_Name || user?.Email || '?').trim().charAt(0).toUpperCase();

  // Per-filter derived state for the unified Users list.
  const filterMeta = {
    activated: { search: searchActivated, setSearch: setSearchActivated, list: filteredActivated, selected: selectedActivated, setSelected: setSelectedActivated, toggle: toggleActivated },
    massDeactivated: { search: searchMassDeactivated, setSearch: setSearchMassDeactivated, list: filteredMassDeactivated, selected: selectedMassDeactivated, setSelected: setSelectedMassDeactivated, toggle: toggleMassDeactivated },
    manuallyDeactivated: { search: searchManuallyDeactivated, setSearch: setSearchManuallyDeactivated, list: filteredManuallyDeactivated, selected: selectedManuallyDeactivated, setSelected: setSelectedManuallyDeactivated, toggle: toggleManuallyDeactivated }
  };
  const current = filterMeta[userFilter];

  const groupActions = {
    activated: [
      { key: 'mass-unlock', label: 'Mass Unlock All', className: 'btn-danger', onClick: handleMassDeactivate, disabled: processing || config.activated.length === 0, icon: (
        <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" /></svg>
      ) },
      { key: 'unlock-selected', label: `Unlock Selected (${selectedActivated.size})`, className: 'btn-warning', onClick: handleManualDeactivate, disabled: processing || selectedActivated.size === 0, icon: (
        <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      ) }
    ],
    massDeactivated: [
      { key: 'mass-activate', label: 'Mass Gets Checked All', className: 'btn-success', onClick: handleMassActivate, disabled: processing || config.massDeactivated.length === 0, icon: (
        <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
      ) },
      { key: 'activate-selected', label: `Gets Checked Selected (${selectedMassDeactivated.size})`, className: 'btn-primary', onClick: handleActivateFromMassDeactivated, disabled: processing || selectedMassDeactivated.size === 0, icon: (
        <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      ) }
    ],
    manuallyDeactivated: [
      { key: 'mass-activate-manual', label: 'Mass Gets Checked All', className: 'btn-success', onClick: handleMassActivateManual, disabled: processing || config.manuallyDeactivated.length === 0, icon: (
        <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
      ) },
      { key: 'activate-selected-manual', label: `Gets Checked Selected (${selectedManuallyDeactivated.size})`, className: 'btn-primary', onClick: handleActivateFromManual, disabled: processing || selectedManuallyDeactivated.size === 0, icon: (
        <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      ) }
    ]
  };

  const allVisibleSelected = current.list.length > 0 && current.list.every(email => current.selected.has(email));
  const someVisibleSelected = current.list.some(email => current.selected.has(email));

  const handleSelectAllToggle = () => {
    current.setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        current.list.forEach(email => next.delete(email));
      } else {
        current.list.forEach(email => next.add(email));
      }
      return next;
    });
  };

  const tabTitles = {
    users: ['User Management', 'Control who gets checked for missing weekly timelogs'],
    caps: ['Non-Billable Caps', 'Set yearly non-billable hour caps per employee'],
    access: ['Timelog Access', 'Grant delegated access to calendars and timelogs'],
    reminders: ['Reminders', 'Choose which weekdays each employee is emailed a missing-timelog reminder'],
    'nav-queue': ['Nav Queue', 'Timelogs that have not reached CapLegal, and the ones Nav rejected'],
    'release-notes': ['Release Notes', 'What changed in the calendar and in this dashboard, per release']
  };
  const [pageTitle, pageSubtitle] = tabTitles[activeTab];

  return (
    <div className="app-shell" data-theme={theme}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark"><img src="/mw-logo.png" alt="MW" /></div>
          <div>
            <div className="brand-title">Portal Admin</div>
            <div className="brand-subtitle">MW internal tools</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`sidebar-nav-item${activeTab === 'users' ? ' sidebar-nav-item--active' : ''}`}
            onClick={() => selectTab('users')}
          >
            <IconUsers />
            User Management
          </button>
          <button
            className={`sidebar-nav-item${activeTab === 'caps' ? ' sidebar-nav-item--active' : ''}`}
            onClick={() => selectTab('caps')}
          >
            <IconClock />
            Non Billable Caps
          </button>
          <button
            className={`sidebar-nav-item${activeTab === 'access' ? ' sidebar-nav-item--active' : ''}`}
            onClick={() => selectTab('access')}
          >
            <IconKey />
            Timelog Access
          </button>
          <button
            className={`sidebar-nav-item${activeTab === 'reminders' ? ' sidebar-nav-item--active' : ''}`}
            onClick={() => selectTab('reminders')}
          >
            <IconBell />
            Reminders
          </button>
          <button
            className={`sidebar-nav-item${activeTab === 'nav-queue' ? ' sidebar-nav-item--active' : ''}`}
            onClick={() => selectTab('nav-queue')}
          >
            <IconSend />
            Nav Queue
          </button>
          <button
            className={`sidebar-nav-item${activeTab === 'release-notes' ? ' sidebar-nav-item--active' : ''}`}
            onClick={() => selectTab('release-notes')}
          >
            <IconNotes />
            Release Notes
          </button>
        </nav>

        <div className="sidebar-footer">
          {themeToggleButton}
          <div className="sidebar-user">
            <span className="sidebar-user-avatar">{userInitial}</span>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.Display_Name || user?.Email}</div>
            </div>
            <button className="icon-btn" onClick={handleLogout} title="Sign out">
              <IconSignOut />
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <header className="main-header">
          <div>
            <h1>{pageTitle}</h1>
            <p className="subtitle">{pageSubtitle}</p>
          </div>
        </header>

        {error && (
          <div className="error-banner">
            <div className="error-content">
              <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <strong>Error:</strong> {error}
            </div>
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        )}

        {activeTab === 'users' && (
          <>
            <div className="stat-row">
              {Object.entries(USER_GROUPS).map(([key, meta]) => (
                <button
                  key={key}
                  className={`stat-tile ${meta.tileClass}${userFilter === key ? ' stat-tile--active' : ''}`}
                  onClick={() => setUserFilter(key)}
                >
                  <span className="stat-tile-icon">
                    {key === 'activated' ? <IconUsers /> : key === 'massDeactivated' ? <IconClock /> : <IconKey />}
                  </span>
                  <span>
                    <span className="stat-tile-value">{config[key].length}</span>
                    <span className="stat-tile-label">{meta.label}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="list-card">
              <div className="list-toolbar">
                <div className="search-box">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder={`Search ${USER_GROUPS[userFilter].label.toLowerCase()}...`}
                    value={current.search}
                    onChange={(e) => current.setSearch(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="action-buttons">
                  {groupActions[userFilter].map(action => (
                    <button
                      key={action.key}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={`btn ${action.className}`}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="select-all-row">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={handleSelectAllToggle}
                    disabled={processing || current.list.length === 0}
                  />
                  Select all ({current.list.length})
                </label>
                {current.selected.size > 0 && (
                  <span className="selected-count">{current.selected.size} selected</span>
                )}
              </div>

              <div className="user-table">
                {current.list.length === 0 ? (
                  <div className="empty-state">
                    <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p>{current.search ? 'No users found' : USER_GROUPS[userFilter].emptyLabel}</p>
                  </div>
                ) : (
                  current.list.map(email => (
                    <label key={email} className="user-row">
                      <input
                        type="checkbox"
                        checked={current.selected.has(email)}
                        onChange={() => current.toggle(email)}
                        disabled={processing}
                      />
                      <span className="user-avatar">{emailInitials(email)}</span>
                      <span className="user-email">{email}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'caps' && (
          <div className="caps-container">
            <div className="caps-toolbar">
              <input
                type="text"
                placeholder="Search employees..."
                value={searchCaps}
                onChange={e => setSearchCaps(e.target.value)}
                className="search-input"
                style={{ maxWidth: '320px' }}
              />
            </div>
            {loadingEmployees ? (
              <div className="loading" style={{ padding: '40px' }}>
                <div className="loading-spinner" />
                <p>Loading employees from CRM...</p>
              </div>
            ) : (
            <table className="caps-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Non-Billable Cap (h/year)</th>
                </tr>
              </thead>
              <tbody>
                {filteredCapEmails.length === 0 ? (
                  <tr><td colSpan={2} className="caps-empty">No employees found</td></tr>
                ) : (
                  filteredCapEmails.map(email => {
                    const emp = employees[email] || {};
                    const capVal = editingCap[email] !== undefined
                      ? editingCap[email]
                      : (emp.Non_billable_hours_yearly_cap !== null && emp.Non_billable_hours_yearly_cap !== undefined
                          ? String(emp.Non_billable_hours_yearly_cap)
                          : '');
                    const isSaving = savingCap.has(email);
                    return (
                      <tr key={email}>
                        <td>
                          <div className="caps-name">{emp.name || email}</div>
                          {emp.name && <div className="caps-email">{email}</div>}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="No cap"
                            value={capVal}
                            onChange={e => handleCapChange(email, e.target.value)}
                            onBlur={() => handleCapSave(email)}
                            onKeyDown={e => { if (e.key === 'Enter') handleCapSave(email); }}
                            disabled={isSaving}
                            className="caps-input"
                            title="Non-billable hours yearly cap"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            )}
          </div>
        )}

        {activeTab === 'access' && (
          <div className="access-container">
            <section className="access-form-card">
              <div className="section-heading">
                <h2>Grant Timelog Access</h2>
                <p className="section-hint">
                  Let one employee's calendar and timelogs be viewed — or edited — by a delegate.
                </p>
              </div>
              <form className="access-form" onSubmit={handleCreateGrant}>
                <div className="access-form-row">
                  <EmployeePicker
                    id="grant-employee"
                    label="Employee"
                    placeholder={loadingEmployees ? 'Loading employees…' : 'Whose timelogs...'}
                    options={employeeOptions}
                    value={grantEmployeeEmails}
                    onChange={setGrantEmployeeEmails}
                    excludeEmail={grantDelegateEmail}
                    disabled={grantSubmitting || loadingEmployees}
                    allowSelectAll
                    selectAllLabel="Select all employees"
                    multi
                  />
                  <div className="access-form-arrow">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                  <EmployeePicker
                    id="grant-delegate"
                    label="Delegate"
                    placeholder={loadingEmployees ? 'Loading employees…' : '...are shared with'}
                    options={employeeOptions}
                    value={grantDelegateEmail}
                    onChange={handleDelegateChange}
                    disabled={grantSubmitting || loadingEmployees}
                  />
                  <div className="access-level-field">
                    <label className="field-label" htmlFor="grant-level">Access level</label>
                    <select
                      id="grant-level"
                      className="access-level-select"
                      value={grantAccessLevel}
                      onChange={e => setGrantAccessLevel(e.target.value)}
                      disabled={grantSubmitting}
                    >
                      <option value={ACCESS_LEVEL_READ}>Read only</option>
                      <option value={ACCESS_LEVEL_READ_WRITE}>Read &amp; write</option>
                    </select>
                  </div>
                </div>

                {grantError && <div className="form-message form-message--error">{grantError}</div>}
                {grantSuccess && <div className="form-message form-message--success">{grantSuccess}</div>}

                <div className="access-form-footer">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={grantSubmitting || grantEmployeeEmails.length === 0 || !grantDelegateEmail}
                  >
                    <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {grantSubmitting
                      ? 'Granting…'
                      : grantEmployeeEmails.length > 1
                        ? `Grant access to ${grantEmployeeEmails.length} employees`
                        : 'Grant access'}
                  </button>
                </div>
              </form>
            </section>

            <section className="access-list-card">
              <div className="section-heading section-heading--row">
                <h2>
                  Active Grants
                  <span className="count-badge">{accessGrants.length}</span>
                </h2>
                <input
                  type="text"
                  placeholder="Search grants..."
                  value={searchAccess}
                  onChange={e => setSearchAccess(e.target.value)}
                  className="search-input search-input--compact"
                />
              </div>

              {loadingAccess ? (
                <div className="loading" style={{ padding: '40px' }}>
                  <div className="loading-spinner" />
                  <p>Loading access grants...</p>
                </div>
              ) : filteredAccessGrants.length === 0 ? (
                <div className="empty-state">
                  <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 11-12 0 6 6 0 0112 0zM3 21v-1a4 4 0 014-4h.5" />
                  </svg>
                  <p>{searchAccess ? 'No grants found' : 'No delegated access set up yet'}</p>
                </div>
              ) : (
                <div className="grant-groups">
                  {grantsByDelegate.map(group => {
                    const groupKey = delegateGroupKey(group.delegate);
                    // Searching should surface matches regardless of collapsed state.
                    const isExpanded = Boolean(searchAccess) || expandedDelegates.has(groupKey);
                    return (
                      <div key={groupKey} className="grant-group">
                        <div
                          className="grant-group-header"
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          onClick={() => toggleGroupExpanded(groupKey)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroupExpanded(groupKey); }
                          }}
                        >
                          <svg className={`grant-group-chevron${isExpanded ? ' grant-group-chevron--open' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="grant-group-avatar">{emailInitials(group.delegate.email || group.delegate.name)}</span>
                          <div className="grant-group-heading">
                            <span className="grant-group-name">{group.delegate.name}</span>
                            {group.delegate.email && group.delegate.email !== group.delegate.name && (
                              <span className="grant-group-sub">{group.delegate.email}</span>
                            )}
                          </div>
                          <div className="grant-group-actions">
                            <span className="grant-group-count">
                              {group.items.length} {group.items.length === 1 ? 'employee' : 'employees'}
                            </span>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={(e) => { e.stopPropagation(); handleDeleteGrantGroup(group); }}
                              disabled={deletingGroupKey === groupKey}
                              title={`Revoke all access for ${group.delegate.name}`}
                            >
                              <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Revoke all
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <ul className="grant-employee-list">
                            {group.items.map(grant => (
                              <li key={grant.id} className="grant-employee-row">
                                <span className="grant-employee-name">{grant.employee.name}</span>
                                <span className={`access-badge ${grant.accessLevel === ACCESS_LEVEL_READ_WRITE ? 'access-badge--write' : 'access-badge--read'}`}>
                                  {grant.accessLevel === ACCESS_LEVEL_READ_WRITE ? 'Read & write' : 'Read only'}
                                </span>
                                <button
                                  className="btn btn-ghost btn-icon-only"
                                  onClick={() => handleDeleteGrant(grant)}
                                  disabled={deletingGrantId === grant.id}
                                  title="Revoke access"
                                >
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
        {activeTab === 'reminders' && (
          <div className="reminders-container">
            <div className="reminders-toolbar">
              <div className="search-box">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchReminders}
                  onChange={e => setSearchReminders(e.target.value)}
                  className="search-input"
                />
              </div>
              <span className="reminders-hint">Click a day to switch it on or off. Changes save straight away.</span>
            </div>

            <div className="select-all-row">
              <label className="select-all-label">
                <input
                  type="checkbox"
                  checked={allRemindersSelected}
                  ref={el => { if (el) el.indeterminate = !allRemindersSelected && someRemindersSelected; }}
                  onChange={handleReminderSelectAllToggle}
                  disabled={reminderRows.length === 0}
                />
                Select all ({reminderRows.length})
              </label>
              {selectedReminders.size > 0 && (
                <span className="selected-count">{selectedReminders.size} selected</span>
              )}
            </div>

            {selectedReminders.size > 0 && (
              <div className="reminders-bulk">
                <span className="reminders-bulk-label">
                  Set for {selectedReminders.size} selected
                </span>
                <div className="day-pills">
                  {REMINDER_DAYS.map(d => (
                    <button
                      key={d.value}
                      type="button"
                      className={`day-pill${draftReminderDays.includes(d.value) ? ' day-pill--on' : ''}`}
                      onClick={() => toggleDraftReminderDay(d.value)}
                      aria-pressed={draftReminderDays.includes(d.value)}
                      title={d.value}
                    >
                      {d.short}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setDraftReminderDays(
                    draftReminderDays.length === ALL_REMINDER_DAYS.length ? [] : ALL_REMINDER_DAYS
                  )}
                >
                  {draftReminderDays.length === ALL_REMINDER_DAYS.length ? 'Clear all' : 'All days'}
                </button>
                <span className="reminders-bulk-summary">
                  {draftReminderDays.length === 0
                    ? 'No days picked — applying turns reminders off.'
                    : `${draftReminderDays.length} ${draftReminderDays.length === 1 ? 'day' : 'days'} picked.`}
                </span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => applyReminderDays(selectedReminderEmails, draftReminderDays)}
                  disabled={selectedReminderEmails.length === 0}
                >
                  Apply
                </button>
              </div>
            )}

            {loadingEmployees ? (
              <div className="loading" style={{ padding: '40px' }}>
                <div className="loading-spinner" />
                <p>Loading employees from CRM...</p>
              </div>
            ) : (
              <table className="caps-table reminders-table">
                <thead>
                  <tr>
                    <th className="reminders-select-col" />
                    <th>Employee</th>
                    {REMINDER_DAYS.map(d => (
                      <th key={d.value} className="reminders-day-col" title={d.value}>{d.short}</th>
                    ))}
                    <th className="reminders-actions-col">All / none</th>
                  </tr>
                </thead>
                <tbody>
                  {reminderRows.length === 0 ? (
                    <tr>
                      <td colSpan={REMINDER_DAYS.length + 3} className="caps-empty">No employees found</td>
                    </tr>
                  ) : (
                    reminderRows.map(o => {
                      const days = employeeReminderDays(employees[o.email]);
                      const isSaving = savingReminders.has(o.email);
                      const allOn = days.length === ALL_REMINDER_DAYS.length;
                      return (
                        <tr key={o.email} className={isSaving ? 'reminders-row--saving' : undefined}>
                          <td className="reminders-select-col">
                            <input
                              type="checkbox"
                              checked={selectedReminders.has(o.email)}
                              onChange={() => toggleReminderSelected(o.email)}
                              aria-label={`Select ${o.name}`}
                            />
                          </td>
                          <td>
                            <div className="caps-name">{o.name}</div>
                            {o.name !== o.email && <div className="caps-email">{o.email}</div>}
                          </td>
                          {REMINDER_DAYS.map(d => {
                            const on = days.includes(d.value);
                            return (
                              <td key={d.value} className="reminders-day-col">
                                <button
                                  type="button"
                                  className={`day-pill${on ? ' day-pill--on' : ''}`}
                                  onClick={() => toggleReminderDay(o.email, d.value)}
                                  disabled={isSaving}
                                  aria-pressed={on}
                                  aria-label={`${d.value} for ${o.name}`}
                                  title={d.value}
                                >
                                  {d.short}
                                </button>
                              </td>
                            );
                          })}
                          <td className="reminders-actions-col">
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => toggleReminderRowAll(o.email)}
                              disabled={isSaving}
                            >
                              {allOn ? 'None' : 'All'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'nav-queue' && <NavQueue employees={employees} />}

        {/* No props and no employee fetch: the notes are bundled, so this tab
            reads nothing from CRM. That is why it is missing from the employees
            effect above. */}
        {activeTab === 'release-notes' && <ReleaseNotes />}
      </main>
      {groupPendingRevoke && (
        <ConfirmModal
          title="Revoke all access?"
          message={`Revoke all ${groupPendingRevoke.items.length} of ${groupPendingRevoke.delegate.name}'s access grants?`}
          confirmLabel="Revoke all"
          onConfirm={confirmDeleteGrantGroup}
          onCancel={() => setGroupPendingRevoke(null)}
        />
      )}
    </div>
  );
}
